import { jwtDecode } from "jwt-decode";
import { parseISOStrictOr } from "../../helpers/date";
import { expiresAt } from "../../helpers/jwt";
import type { AuthenticatedAccountUser } from "../../types/management-auth";
import type { AuthenticatedUser } from "../types";

/**Get the account_id the most recently used account for an authenticatedUser  */
export const getInitialAccount = (accountUser: AuthenticatedAccountUser) => {
    const mostRecentAccount = accountUser.accounts.reduce(
        (accumulator, account) => {
            if (!accumulator) {
                return account;
            }
            const accumulatorTimestamp = parseISOStrictOr(
                accumulator.user.last_seen_at,
                new Date(0),
            );
            const accountTimestamp = parseISOStrictOr(
                account.user.last_seen_at,
                new Date(0),
            );

            if (accountTimestamp > accumulatorTimestamp) {
                return account;
            }
            return accumulator;
        },
    );

    const mostRecentProdAccountId = `P${mostRecentAccount.account_id.substr(1)}`;
    const mostRecentProdAccount = accountUser.accounts.find(
        (account) => account.account_id === mostRecentProdAccountId,
    );
    return mostRecentProdAccount
        ? mostRecentProdAccountId
        : mostRecentAccount.account_id;
};

export const getAccountUserScope = (
    accountId: string,
    user: AuthenticatedUser,
) => {
    const account = user.account_user.accounts.find(
        (account) => account.account_id === accountId,
    );
    if (!accountId || !account) {
        return [];
    }
    return account.user.scope || [];
};

export const hasAnyOfScopes = (
    accountId: string,
    user: AuthenticatedUser,
    scope: string[][],
): boolean => {
    const userAccountScope = getAccountUserScope(accountId, user);
    return isAllowed(scope, userAccountScope);
};

export const isAllowed = (
    allowedScopes: string[][],
    userScopes: string[],
): boolean => {
    return (
        allowedScopes
            .map((scopeArray) =>
                scopeArray.reduce((acc: boolean, singleScope: string) => {
                    return acc && userScopes.includes(singleScope);
                }, true),
            )
            .find((result) => result === true) || false
    );
};

export const decodeTokenScopes = (token: string | undefined) => {
    if (!token) return [];
    const decoded = jwtDecode(token) as { scopes: string } | undefined;
    if (!decoded || !decoded.scopes) return [];
    return decoded.scopes.split(" ") as string[];
};

export const getAccountIdFromAudience = (aud: string) => {
    const url = new URL(aud);
    return url.username;
};
